import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";

const HERO = "/lookbook-fw26/knit-look-01.png";
const EDITORIAL_FEATURE = "/lookbook-fw26/knit-look-05.png";
const EDITORIAL_1 = "https://images.unsplash.com/photo-1576790807856-b9205fb5703f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const EDITORIAL_2 = "https://images.unsplash.com/photo-1756276900419-868625adff43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Home = () => {
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        fetchProducts().then((data) =>
            setFeatured(data.filter((product) => !product.is_voucher).slice(0, 8))
        );
    }, []);

    return (
        <div data-testid="home-page">
            {/* MARQUEE */}
            <div className="bg-hazard text-white border-y-4 border-ink overflow-hidden">
                <Marquee speed={50} gradient={false} className="py-3">
                    <span className="font-heading text-2xl md:text-4xl uppercase tracking-tight mx-8">
                        FW26 // MANIFESTO ▲ DROPS FRIDAY 09:00 PST ▲ FREE SHIPPING OVER $200 ▲ FW26 // MANIFESTO ▲ DROPS FRIDAY 09:00 PST ▲
                    </span>
                </Marquee>
            </div>

            {/* HERO */}
            <section className="relative border-b-4 border-ink">
                <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
                    <div className="lg:col-span-7 relative aspect-[4/5] lg:aspect-auto lg:min-h-[80vh] order-2 lg:order-1 border-t-2 lg:border-t-0 lg:border-r-4 border-ink overflow-hidden">
                        <img
                            src={HERO}
                            alt="Hero lookbook"
                            className="w-full h-full object-cover object-center scale-[1.08]"
                            data-testid="hero-image"
                        />
                        <span className="absolute bottom-4 left-4 font-mono-ui text-[10px] uppercase tracking-[0.25em] bg-white px-2 py-1 border-2 border-ink">
                            Look 01 / FW26
                        </span>
                    </div>
                    <div className="lg:col-span-5 p-6 md:p-10 lg:p-12 flex flex-col justify-center order-1 lg:order-2 bg-background">
                        <div className="max-w-xl">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-hazard"></span>
                                <span className="font-mono-ui text-xs uppercase tracking-[0.3em]">
                                    Vol. 06 / Manifesto
                                </span>
                            </div>
                            <h1
                                className="font-heading uppercase leading-[0.85] tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[9vw] mt-6"
                                data-testid="hero-title"
                            >
                                BUILT
                                <br />
                                FOR THE
                                <br />
                                <span className="text-hazard">UNQUIET.</span>
                            </h1>
                            <p className="mt-6 max-w-md text-base leading-relaxed">
                                A heavyweight collection cut from concrete, coded for the
                                curfew. No archives. No restocks. Once it&apos;s gone, it stays gone.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/shop"
                                    className="group inline-flex items-center justify-center gap-2 bg-white text-black border-2 border-black px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-hazard hover:border-hazard transition-colors"
                                    data-testid="hero-shop-button"
                                >
                                    Shop The Drop
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/lookbook"
                                    className="inline-flex items-center justify-center gap-2 border-2 border-black px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-black hover:bg-black hover:text-white transition-colors"
                                    data-testid="hero-lookbook-button"
                                >
                                    View Lookbook
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SPEC STRIP */}
            <section className="border-b-4 border-ink bg-bone">
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x-2 md:divide-x-2 divide-ink">
                    {[
                        ["20", "Pieces"],
                        ["500GSM", "Heavyweight"],
                        ["PORTUGAL", "Constructed"],
                        ["1000", "Per Run"],
                    ].map(([n, l]) => (
                        <div key={l} className="p-6 md:p-8 text-center">
                            <div className="font-heading text-4xl md:text-6xl leading-none text-white">
                                {n}
                            </div>
                            <div className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                {l}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="border-b-4 border-ink">
                <div className="px-4 md:px-8 py-10 flex justify-between items-end border-b-2 border-ink">
                    <div>
                        <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            // 01
                        </span>
                        <h2 className="font-heading text-5xl md:text-7xl uppercase leading-none mt-1">
                            Latest Drop
                        </h2>
                    </div>
                    <Link
                        to="/shop"
                        className="hidden md:inline-flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.25em] hover:text-hazard"
                        data-testid="home-view-all-link"
                    >
                        View All <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
                    {featured.map((p, idx) => (
                        <div
                            key={p.id}
                            className={`${idx % 2 === 1 ? "border-l-2" : ""} ${
                                idx % 4 !== 3 ? "lg:border-r-2" : ""
                            } ${idx >= 2 ? "border-t-2" : ""} border-ink`}
                        >
                            <ProductCard product={p} />
                        </div>
                    ))}
                </div>
            </section>

            {/* EDITORIAL SPLIT */}
            <section className="border-b-4 border-ink grid grid-cols-1 md:grid-cols-2">
                <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[600px] border-b-2 md:border-b-0 md:border-r-2 border-ink overflow-hidden">
                    <img src={EDITORIAL_FEATURE} alt="Editorial" className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center bg-white text-black">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-black/40">
                        // 02 / Editorial
                    </span>
                    <h2 className="font-heading text-5xl md:text-7xl uppercase leading-[0.9] mt-4">
                        Concrete <span className="text-hazard">Hours</span> /
                        <br />
                        Lookbook 06
                    </h2>
                    <p className="mt-6 max-w-md leading-relaxed text-black/80">
                        Shot on the loading bays of the East district between 04:00 and
                        sunrise. Eighteen looks, two cities, one curfew.
                    </p>
                    <Link
                        to="/lookbook"
                        className="self-start mt-8 inline-flex items-center gap-2 border-2 border-black px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-black hover:bg-hazard hover:border-hazard hover:text-white"
                        data-testid="home-editorial-link"
                    >
                        Read The Issue <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* CATEGORY GRID */}
            <section className="border-b-4 border-ink">
                <div className="px-4 md:px-8 py-10 border-b-2 border-ink">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        // 03
                    </span>
                    <h2 className="font-heading text-5xl md:text-7xl uppercase leading-none mt-1">
                        Shop By Category
                    </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5">
                    {[
                        { slug: "tops", label: "Tops", img: EDITORIAL_2 },
                        { slug: "bottoms", label: "Bottoms", img: EDITORIAL_1 },
                        { slug: "outerwear", label: "Outerwear", img: HERO },
                        { slug: "footwear", label: "Footwear", img: EDITORIAL_2 },
                        { slug: "accessories", label: "Accessories", img: EDITORIAL_1 },
                    ].map((c, i) => (
                        <Link
                            key={c.slug}
                            to={`/shop/${c.slug}`}
                            className={`relative group aspect-square overflow-hidden border-y-2 border-black ${
                                i !== 4 ? "border-r-2 border-black" : ""
                            }`}
                            data-testid={`home-category-${c.slug}`}
                        >
                            <img
                                src={c.img}
                                alt={c.label}
                                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                            />
                            <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors" />
                            <div className="relative h-full flex items-end p-4 md:p-6">
                                <h3 className="font-heading text-3xl md:text-4xl uppercase text-white">
                                    {c.label}
                                </h3>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="px-4 md:px-8 py-20 md:py-32 text-center bg-background">
                <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                    [The Manifesto]
                </span>
                <h2 className="font-heading text-5xl md:text-8xl uppercase leading-[0.9] mt-6 max-w-5xl mx-auto">
                    We do not chase
                    <br />
                    <span className="text-hazard">trends</span>. We build
                    <br />
                    weapons for the wardrobe.
                </h2>
            </section>
        </div>
    );
};

export default Home;
